export type ToastVariant = 'info' | 'success' | 'error'

export type ToastOptions = {
	id?: string
	variant?: ToastVariant
	durationMs?: number
}

const DEFAULT_TOAST_ID = 'pvolve-toast'
const TOAST_STYLE_ID = 'pvolve-toast-style'
const hideToastTimers = new Map<string, number>()

function ensureToastStyles(): void {
	if (document.getElementById(TOAST_STYLE_ID)) return

	const style = document.createElement('style')
	style.id = TOAST_STYLE_ID
	style.textContent = `
		.pvolve-toast {
			position: fixed;
			left: 50%;
			bottom: 24px;
			z-index: 2147483647;
			display: flex;
			align-items: center;
			gap: 10px;
			width: min(640px, calc(100vw - 24px));
			max-width: calc(100vw - 24px);
			min-height: 44px;
			padding: 12px 18px;
			border: 1px solid var(--color-black-100, #d7d6d6);
			border-radius: 4px;
			background: var(--color-eggshell, #f9f8f4);
			color: var(--color-black-800, #1a1a1a);
			font-family: var(--font-body-family, Muller, sans-serif);
			font-size: 14px;
			font-weight: 400;
			line-height: 1.35;
			letter-spacing: 0;
			box-shadow: 0 8px 24px rgba(26, 26, 26, 0.14);
			pointer-events: none;
			opacity: 0;
			transform: translate(-50%, 12px);
			transition: opacity 180ms ease, transform 180ms ease;
		}

		.pvolve-toast::before {
			content: '';
			width: 8px;
			height: 8px;
			border-radius: 999px;
			background: var(--color-black-800, #1a1a1a);
			flex: 0 0 auto;
		}

		.pvolve-toast.is-visible {
			opacity: 1;
			transform: translate(-50%, 0);
		}

		.pvolve-toast[data-variant='success']::before {
			background: var(--color-yellow, #f6fc8f);
			border: 1px solid var(--color-black-800, #1a1a1a);
		}

		.pvolve-toast[data-variant='error'] {
			border-color: #b3261e;
		}

		.pvolve-toast[data-variant='error']::before {
			background: #b3261e;
		}

		@media (prefers-reduced-motion: reduce) {
			.pvolve-toast {
				transition: none;
			}
		}
	`
	document.head.appendChild(style)
}

export function showToast(message: string, options: ToastOptions = {}): void {
	ensureToastStyles()

	const toastId = options.id ?? DEFAULT_TOAST_ID
	let toast = document.getElementById(toastId)
	if (!toast) {
		toast = document.createElement('div')
		toast.id = toastId
		toast.className = 'pvolve-toast'
		toast.setAttribute('role', 'status')
		toast.setAttribute('aria-live', 'polite')
		document.body.appendChild(toast)
	}

	toast.textContent = message
	toast.dataset.variant = options.variant ?? 'info'
	toast.setAttribute(
		'aria-live',
		options.variant === 'error' ? 'assertive' : 'polite'
	)
	toast.classList.add('is-visible')

	const existingTimer = hideToastTimers.get(toastId)
	if (existingTimer) window.clearTimeout(existingTimer)
	hideToastTimers.set(toastId, window.setTimeout(() => {
		toast?.classList.remove('is-visible')
		hideToastTimers.delete(toastId)
	}, options.durationMs ?? 3200))
}
